import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Mail, Calendar, X, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MeetingRequestDialog } from "../common/MeetingRequestDialog";
import { EmailHubDialog } from "../common/EmailHubDialog";

export function ContactHub() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMeetingOpen, setIsMeetingOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);

  const contactOptions = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      color: "bg-[#25D366]",
      href: "https://wa.me/919137812312?text=Hi%20AccelerateByte!%20I'd%20like%20to%20discuss%20a%20project.",
      description: "Quick Chat",
      action: null
    },
    {
      id: "calendar",
      label: "Book a Call",
      icon: Calendar,
      color: "bg-blue-600",
      href: "#",
      description: "Get Expert Advice",
      action: () => {
        setIsMeetingOpen(true);
        setIsOpen(false);
      }
    },
    {
      id: "email",
      label: "Email Us",
      icon: Mail,
      color: "bg-purple-600",
      href: "#",
      description: "Smart Contact",
      action: () => {
        setIsEmailOpen(true);
        setIsOpen(false);
      }
    }
  ];

  return (
    <>
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end gap-4">
        <AnimatePresence>
          {isOpen && (
            <div className="flex flex-col items-end gap-3 mb-2">
              {contactOptions.map((option, index) => (
                <motion.button
                  key={option.id}
                  initial={{ opacity: 0, x: 20, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 20, scale: 0.8 }}
                  transition={{ delay: index * 0.1 }}
                  className="group flex items-center gap-3 cursor-pointer bg-transparent border-none p-0 outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
                  aria-label={option.label}
                  onClick={() => {
                    if (option.action) {
                      option.action();
                    } else {
                      window.open(option.href, "_blank");
                    }
                  }}
                >
                  <div className="bg-background/80 backdrop-blur-md border border-border/50 px-3 py-1.5 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <p className="text-xs font-bold leading-tight text-foreground text-right">{option.label}</p>
                    <p className="text-[10px] text-muted-foreground leading-tight text-right">{option.description}</p>
                  </div>
                  <div className={`${option.color} text-white p-3.5 rounded-full shadow-lg hover:scale-110 transition-transform flex items-center justify-center`}>
                    <option.icon className="h-6 w-6" />
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </AnimatePresence>

        <Button
          size="icon"
          aria-label={isOpen ? "Close contact options" : "Open contact options"}
          className={`h-14 w-14 rounded-full shadow-2xl transition-all duration-300 ${
            isOpen ? "bg-muted text-foreground hover:bg-muted" : "bg-primary text-primary-foreground hover:scale-110"
          }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
              >
                <X className="h-6 w-6" />
              </motion.div>
            ) : (
              <motion.div
                key="chat"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                className="relative"
              >
                <MessageSquare className="h-6 w-6" />
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </Button>
      </div>

      <MeetingRequestDialog 
        isOpen={isMeetingOpen} 
        onClose={() => setIsMeetingOpen(false)} 
      />

      <EmailHubDialog
        isOpen={isEmailOpen}
        onClose={() => setIsEmailOpen(false)}
      />
    </>
  );
}
